import { useEffect, useState } from "react";
import { getPassage } from "../../services/passageService";
import { isCanceledError } from "../../utils/examError";

// Passage details (with body) already fetched, least recently used first.
const cache = new Map();
const listeners = new Set();

const keep = (detail) => {
  cache.delete(detail.uuid);
  cache.set(detail.uuid, detail);
  if (cache.size > 20) cache.delete(cache.keys().next().value);
};

// After a save: every open panel of this passage shows the new detail.
export const rememberPassage = (detail) => {
  if (!detail?.uuid) return;
  keep(detail);
  listeners.forEach((listener) => listener(detail));
};

export const forgetPassage = (uuid) => {
  cache.delete(uuid);
};

/** The passage's detail ({..., body, questions, usage}), fetched once and shared; null uuid = nothing. */
export default function usePassage(uuid) {
  const [state, setState] = useState({ data: null, loading: false, error: null });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!uuid) {
      setState({ data: null, loading: false, error: null });
      return undefined;
    }
    const cached = cache.get(uuid);
    if (cached) {
      keep(cached);
      setState({ data: cached, loading: false, error: null });
      return undefined;
    }
    const controller = new AbortController();
    setState({ data: null, loading: true, error: null });
    getPassage(uuid, { signal: controller.signal })
      .then((data) => {
        keep(data);
        setState({ data, loading: false, error: null });
      })
      .catch((err) => {
        if (!isCanceledError(err)) setState({ data: null, loading: false, error: err });
      });
    return () => controller.abort();
  }, [uuid, attempt]);

  useEffect(() => {
    if (!uuid) return undefined;
    const onSaved = (detail) => {
      if (detail.uuid === uuid) setState({ data: detail, loading: false, error: null });
    };
    listeners.add(onSaved);
    return () => listeners.delete(onSaved);
  }, [uuid]);

  return { ...state, retry: () => setAttempt((n) => n + 1) };
}
